import * as Sentry from '@sentry/react'
import { store } from '@redux/store'
import { clearPersistedState } from '@redux/persistence'
import { addNoticeAction }    from '@redux/notices/actions'
import { fetchSessionAction } from '@redux/user/fetch'
import { HEADERS, PATHS } from '@helpers/javascript/config/CONSTANTS'
import { todayIso } from '@helpers/javascript/date_helpers'

const unauthenticatedMessages = () => Object.keys(I18n.translations).map(locale =>
  I18n.t("devise.failure.unauthenticated", { locale })
)

const limitedMessages = () => Object.keys(I18n.translations).map(locale =>
  I18n.t("users_permissions.session_limited", { locale })
)

export const csrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta.getAttribute("content")
}

const setCsrfToken = token => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  if (meta && token) meta.setAttribute('content', token)
}

const refreshCsrfToken = () => (
  fetch(PATHS.sessions.csrf, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
  .then(response => response.json())
  .then(data => setCsrfToken(data.form_authenticity_token))
)

// A page served offline by the service worker carries the token cached at install.
const fetchWithCsrf = send => send().then(response => {
  if (response.status != 422) return response

  return response.clone().json()
  .then(data => data && data.invalid_token ? refreshCsrfToken().then(send) : response)
  .catch(() => response)
})

const getHeaders = () => {
  const headers = { ...HEADERS }
  headers['X-CSRF-Token'] = csrfToken()
  return headers
}

const unauthenticated = error => unauthenticatedMessages().includes(error)
const limited         = error => limitedMessages().includes(error)

export const shouldAlertError = errorMessage => {
  if (!errorMessage) return false

  const ignoreList = [
    "Internal Server Error",
    "Unprocessable Entity",
    "Unprocessable Content",
    "Not Found",
    "Load failed",
    "Timeout"
  ]

  return !ignoreList.some(partial => errorMessage.includes(partial));
}

const shouldRaiseSentry = error => {
  if (error.name == "Error") return false

  const ignoreList = [
    "Unexpected",
    "Failed to fetch",
    "Load failed",
    ...unauthenticatedMessages(),
    ...limitedMessages()
  ]

  return !ignoreList.some(partial => error.message.includes(partial));
}

const resolveRequest = (resolve, data) => {
  if (data && data.hasOwnProperty('error')) {
    if(unauthenticated(data.error)) {
      const user = store.getState().user
      const { id, unique_session_id } = user
      store.dispatch(fetchSessionAction(id, unique_session_id))
    } else if (limited(data.error)) {
      window.location.href = "/limit_session"
    } else if (shouldAlertError(data.error)) {
      store.dispatch(addNoticeAction({ message: data.error, type: data.level || 'error' }))
    }

    throw new Error(data.error)
  } else if (data && data.hasOwnProperty('notice')) {
    store.dispatch(addNoticeAction({ message: data.notice, type: 'notice' }))
  } else if (data && data.hasOwnProperty('success')) {
    store.dispatch(addNoticeAction({ message: data.success, type: 'success' }))
  }

  return resolve(data)
}

const rejectRequest = (reject, error) => {
  if(shouldRaiseSentry(error)) Sentry.captureException(error)
  reject(error)
}

const parseResponse = response => {
  const isJson = (response.headers.get('content-type') || '').includes('json')
  if (isJson) return response.json()
  if (!response.ok) throw new Error('Load failed')
  return {}
}

export const getRequest = url => {
  return new Promise((resolve, reject) => {
    fetch(url,
      {
        headers:     getHeaders(),
        credentials: 'same-origin'
      }
    )
    .then(parseResponse)
    .then(data     => resolveRequest(resolve, data))
    .catch(error   => rejectRequest(reject, error))
  })
}

export const bodyRequest = ({ method = 'POST', url, body = {} }) => {
  return new Promise((resolve, reject) => {
    fetchWithCsrf(() => fetch(url,
      {
        method:      method,
        headers:     getHeaders(),
        credentials: 'same-origin',
        body:        JSON.stringify(body)
      }
    ))
    .then(parseResponse)
    .then(data     => resolveRequest(resolve, data))
    .catch(error   => rejectRequest(reject, error))
  })
}

export const postRequest = ({ url, body = {} }) => (
  bodyRequest({ method: 'POST', url, body })
)

export const patchRequest = ({ url, body = {} }) => (
  bodyRequest({ method: 'PATCH', url, body })
)

export const deleteRequest = url => bodyRequest({ method: 'DELETE', url })

export const formRequest = ({ method = 'POST', url, form }) => {
  return new Promise((resolve, reject) => {
    const send = () => {
      const headers = getHeaders()
      form.set("authenticity_token", csrfToken())
      delete headers['Content-Type']
      return fetch(url,{ method, headers, credentials: 'same-origin', body: form })
    }
    fetchWithCsrf(send)
    .then(parseResponse)
    .then(data     => resolveRequest(resolve, data))
    .catch(error   => rejectRequest(reject, error))
  })
}

export const parseParams = string => {
  if (string == '') return {}

  const parsed = {}

  string   = string.substring(string.indexOf('?') + 1)
  const p1 = string.split('&')
  p1.map((value) => {
    const params = value.split('=')
    parsed[params[0]] = params[1]
  })

  return parsed
}

export const parameterize = object => {
  const params = new URLSearchParams
  Object.keys(object).forEach(key => {
    object[key] && params.set(key, object[key])
  })
  return params
}

export const pathname = ({ path, format, params = {} }) => {
  const url = new URL(path, window.location.origin)
  if (format) { url.pathname = url.pathname.replace(/\..*$/,"") + `.${format}`}
  Object.keys(params).forEach(key => url.searchParams.set(key, params[key]))
  return url.pathname + url.search
}

export const getSearch = parameter => {
  const searchParams = new URLSearchParams(window.location.search)
  return searchParams.get(parameter)
}

// This is a fucking spaguetti, I should refactor it when i have time
// Basically it takes a filter state object and returns the corresponding url
export const parameterizeFilter = filter => {
  const filterObject = {}
  // Date or status
  if (filter.search && filter.search !== '') {
    filterObject.search = filter.search
    if (filter.page !== '') {
      filterObject.page = filter.page
    }
  } else {
    if (filter.status === 'date') {
      if (filter.date !== todayIso()) { filterObject.date = filter.date }
    } else {
      filterObject.status = filter.status
    }
    if (filter.technician) {
      filterObject.technician = filter.technician
    }
    if (filter.category) {
      filterObject.category = filter.category
    }
    if (filter.page !== '') {
      filterObject.page = filter.page
    }
    if (filter.workorder_type) {
      const typeArray = new Array
      Object.keys(filter.workorder_type).forEach(key => {
        if (filter.workorder_type[key]) typeArray.push(key)
      })
      filterObject.workorder_type = typeArray
    }
    if (filter.by_distance) {
      filterObject.by_distance  = filter.by_distance
    }
    filter.fields.forEach(field => {
      filterObject[`fields[${field.id}]`] = field.answer
    })
  }

  // From object to url params
  return filterObject
}

export const serializeFilterParams = (obj, prefix = "") => {
  const queryParts = []

  const build = (value, keyPath) => {
    if (value === null || value === undefined) return

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === "object") {
          build(item, `${keyPath}[${index}]`)
        } else {
          queryParts.push(`${encodeURIComponent(keyPath)}[]=${encodeURIComponent(item)}`)
        }
      })
    } else if (typeof value === "object") {
      Object.entries(value).forEach(([k, v]) => {
        build(v, `${keyPath}[${k}]`)
      })
    } else {
      queryParts.push(`${encodeURIComponent(keyPath)}=${encodeURIComponent(value)}`)
    }
  }

  Object.entries(obj).forEach(([key, val]) => {
    build(val, prefix ? `${prefix}[${key}]` : key)
  })

  return queryParts.join("&")
}

const clearCaches = () => {
  if (!window.caches) return Promise.resolve()

  return caches.keys()
    .then(cacheKeys => Promise.all(cacheKeys.map(cacheKey => caches.delete(cacheKey))))
}

const unregisterServiceWorkers = () => {
  if (!navigator.serviceWorker) return Promise.resolve()

  return navigator.serviceWorker.getRegistrations()
    .then(registrations => Promise.all(registrations.map(registration => registration.unregister())))
}

export const clearLocalData = () => {
  if (!confirm(I18n.t("mobile.actions.clear_cache_confirm"))) return

  localStorage.clear()

  // The store keeps writing while the page lives, so the persisted state is dropped last.
  clearCaches()
    .catch(() => {})
    .then(unregisterServiceWorkers)
    .catch(() => {})
    .then(clearPersistedState)
    .catch(() => {})
    .then(() => window.location.reload())
}

export const buildReferrerPath = referrer => {
  switch(referrer?.type) {
    case 'workorder': return `/mobile/interventions/${referrer.id}`
    case 'demand':    return `/mobile/demands/${referrer.id}`
    case 'demands':   return referrer.demand_filter ? `/mobile/demands${referrer.demand_filter}` : '/mobile/demands'
    case 'invoice':   return `/mobile/invoices/${referrer.id}`
    case 'site':      return `/mobile/sites/${referrer.id}`
    case 'material':  return `/mobile/materials/${referrer.id}`
    case 'map':       return referrer.date ? `/mobile/map?date=${referrer.date}` : '/mobile/map'
    default:          null
  }
}

export const truncateString = (str, maxLength) => {
  if(!str) return ''

  if (str.length <= maxLength) {
    return str
  }
  return str.slice(0, maxLength) + '...'
}

export const navigatorLocale = () => {
  const locale = navigator.language || navigator.userLanguage
  return locale.split("-")[0]
}

const parse      = string => string.normalize().toLowerCase()
export const hit = (target, string) => parse(target).includes(parse(string))

export const calcDist = (origin, destination) => {
  const R     = 6371 * 1000 // km
  const lat1  = origin.latitude
  const lon1  = origin.longitude
  const lat2  = destination.latitude
  const lon2  = destination.longitude
  const dLat  = toRad(lat2-lat1)
  const dLon  = toRad(lon2-lon1)
  const rLat1 = toRad(lat1)
  const rLat2 = toRad(lat2)

  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(rLat1) * Math.cos(rLat2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  const d = R * c
  return d.toFixed(0)
}

const toRad = value => value * Math.PI / 180
